import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import { Link as RouterLink } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useDownloadWithPolicy } from '../../../common/DownloadPolicyDialog'
import { C, RADIUS, SX, TYPE } from '../../merchants/shared/tokens'
import { saveBlob, filenameFromResponse } from '../../freight-profitability/freightPnlUtils'
import { KIND, download, errorText, fetchTatGrid, switchToStandard } from '../ptlSetupApi'
import { fmtDate, num, ROUTES, tatBaseName } from '../constants'
import { Banner } from '../components/SetupUi'
import HistoryDialog from '../components/HistoryDialog'
import TatUpload from './TatUpload'
import CustomLanes, { customLanes } from './CustomLanes'
import TatBasis, { baseSummary } from './TatBasis'

/** One merchant's TAT grid (GET tat/grid): every standard lane with its custom value where set. Null id = skip. */
export function useTat(merchantId) {
  const [grid, setGrid] = useState(null)
  const [error, setError] = useState('')
  const reload = useCallback(() => {
    if (!merchantId) return
    setError('')
    fetchTatGrid(merchantId).then(setGrid).catch((e) => setError(errorText(e, 'Could not load the TAT')))
  }, [merchantId])
  useEffect(() => { setGrid(null); reload() }, [reload])
  return { grid, error, reload }
}

/** A courier code (B2B_BLUEDART_INT) that wraps after its underscores, not mid-word, in a narrow KPI card. */
const breakAtUnderscores = (code) => code.split('_').map((part, i, all) => (
  <React.Fragment key={i}>{part}{i < all.length - 1 && <>_<wbr /></>}</React.Fragment>
))

/** KPI cards of Step 2 (plan screen 3): the TAT basis, lanes custom, faster and slower than the base. */
export function tatKpis(grid) {
  if (!grid) return ['TAT basis', 'Lanes custom', 'Faster than base', 'Slower than base'].map((label) => ({ key: label, label, value: '' }))
  const custom = customLanes(grid)
  const faster = custom.filter((c) => c.diff.tone === 'faster').length
  const slower = custom.filter((c) => c.diff.tone === 'slower').length
  const base = grid.basisCourier ? 'base' : 'standard'
  return [
    grid.basisCourier
      ? { key: 'basis', label: 'TAT basis', value: <>{breakAtUnderscores(grid.basisCourier)} TAT</>, sub: `${num(grid.courierLanes)} courier lanes, ${num(grid.standardLanes)} on the standard` }
      : { key: 'basis', label: 'TAT basis', value: 'Standard TAT', sub: `${num(grid.standardLanes)} / ${num(grid.cells.length)} lanes on the standard` },
    { key: 'custom', label: 'Lanes custom', value: num(grid.customLanes), sub: grid.customLanes ? 'From the agreement' : grid.basisCourier ? 'None: courier TAT or standard on each lane' : 'None: standard on every lane' },
    { key: 'faster', label: `Faster than ${base}`, value: num(faster), tone: faster ? 'warn' : undefined, sub: `Custom MAX below the ${base} MAX` },
    { key: 'slower', label: `Slower than ${base}`, value: num(slower), sub: `Custom MAX above the ${base} MAX` },
  ]
}

/**
 * Step 2 TAT body (plan screen 3). Two parts:
 * 1. TAT basis: the Prozo standard (default) or one courier's own TAT (Merchant TAT → Courier TAT), for a merchant that
 *    ships with that courier only. Lanes the courier has not given follow the standard. POST tat/basis.
 * 2. Custom TAT: lanes from the agreement (optional), on top of the basis. Uploaded in their own view (download current →
 *    edit → check → review against the base → confirm). Every change is in the History (audit log); there is no rollback.
 */
export default function TatStep({ merchant, grid, error, reload, onChanged, onUploadView }) {
  const { enqueueSnackbar } = useSnackbar()
  const { triggerDownload, PolicyDialog } = useDownloadWithPolicy()
  const [uploading, setUploading] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  // The step footer (Next) hides while the upload view is open: one job on screen at a time.
  useEffect(() => { if (onUploadView) onUploadView(uploading) }, [uploading, onUploadView])

  const lanes = useMemo(() => customLanes(grid), [grid])
  const custom = lanes.length > 0
  const noStandard = grid && grid.cells.length === 0
  const basis = grid?.basisCourier || ''

  const getCurrent = async () => {
    try {
      const res = await download(KIND.TAT, merchant.id)
      saveBlob(res.data, filenameFromResponse(res, `ptl_tat_${merchant.id}.csv`))
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Could not download the TAT'), { variant: 'error' })
    }
  }
  // Every download asks for the download policy first (as the other Merchant TAT screens).
  const downloadCurrent = () => triggerDownload(getCurrent, `${merchant.name} TAT`)

  const reset = async () => {
    setBusy(true)
    try {
      await switchToStandard(KIND.TAT, merchant.id)
      enqueueSnackbar(`Custom lanes removed: those lanes follow ${tatBaseName(basis)} again`, { variant: 'success' })
      setResetOpen(false)
      reload()
      onChanged()
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Could not remove the custom lanes'), { variant: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const changed = () => { setUploading(false); reload(); onChanged() }

  if (error) return <Banner tone='danger' action={<Button size='small' onClick={reload} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>
  if (!grid) return <Typography role='status' sx={{ ...TYPE.rowHelp, py: 4, textAlign: 'center' }}>Loading TAT…</Typography>
  if (noStandard) {
    return (
      <Banner tone='warn' action={<Button size='small' component={RouterLink} to={ROUTES.tat} sx={{ textTransform: 'none' }}>Open Merchant TAT</Button>}>
        The Prozo standard TAT is not uploaded yet. Every PTL TAT (courier and custom) uses its lanes, so upload the standard first.
      </Banner>
    )
  }
  if (uploading) {
    return (
      <>
        <TatUpload merchant={merchant} grid={grid} onDownload={downloadCurrent} onCancel={() => setUploading(false)} onSaved={changed} />
        {PolicyDialog}
      </>
    )
  }

  // Third card: custom lanes are not a basis but sit on top of it, so this is an action card, not a radio.
  const customCard = (
    <Box sx={{
      boxSizing: 'border-box', p: 2, minHeight: 96, borderRadius: RADIUS.md, display: 'flex', flexDirection: 'column', gap: 1,
      border: `1px solid ${custom ? C.accent : C.line}`, bgcolor: C.surface,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TuneOutlinedIcon aria-hidden sx={{ fontSize: 18, color: custom ? C.accent : C.inkMuted }} />
        <Typography component='span' sx={{ ...TYPE.rowLabel, fontSize: 14.5 }}>Custom TAT</Typography>
        <Box component='span' sx={custom
          ? { ...SX.pill, ml: 'auto', color: C.ok, bgcolor: C.okSoft, border: `1px solid ${C.okBorder}` }
          : { ...SX.pill, ml: 'auto', color: C.inkMuted, bgcolor: C.surfaceAlt, border: `1px solid ${C.line}` }}>
          {custom ? `${num(lanes.length)} in use` : 'Optional'}
        </Box>
      </Box>
      <Typography component='span' sx={{ ...TYPE.rowHelp, display: 'block' }}>
        On top of the base: only the lanes where the agreement sets its own TAT. They win on those lanes.
      </Typography>
      <Box sx={{ mt: 'auto' }}>
        {custom ? (
          <Button size='small' onClick={() => document.getElementById('ptl-custom-lanes')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            sx={{ ...SX.ctaGhost, minHeight: 32 }} variant='outlined'>
            Review custom TAT
          </Button>
        ) : (
          <Button size='small' variant='outlined' startIcon={<UploadFileOutlinedIcon />} onClick={() => setUploading(true)} sx={{ ...SX.ctaGhost, minHeight: 32 }}>
            Set a custom TAT
          </Button>
        )}
      </Box>
    </Box>
  )

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <TatBasis merchant={merchant} grid={grid} onSaved={() => { reload(); onChanged() }} extra={customCard} />
      </Box>

      {!custom ? (
        <Box sx={{ ...SX.cardFlush, p: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <CheckCircleIcon sx={{ color: C.ok, fontSize: 20 }} aria-hidden />
          <Typography sx={{ ...TYPE.rowHelp, color: C.inkBody }}>
            {merchant.name} has no custom TAT: it uses {baseSummary(grid)}. Add one only if the agreement sets its own TAT.
          </Typography>
        </Box>
      ) : (
        <>
          <Box id='ptl-custom-lanes' sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5, scrollMarginTop: 80 }}>
            <Box sx={{ flex: '1 1 260px' }}>
              <Typography component='h2' sx={TYPE.sectionTitle}>Custom TAT from the agreement</Typography>
              <Typography sx={{ ...TYPE.rowHelp, mt: 0.25 }}>Only the lanes where the agreement sets its own TAT; they win over {tatBaseName(basis)}.</Typography>
            </Box>
            <Button variant='outlined' startIcon={<DownloadOutlinedIcon />} onClick={downloadCurrent} sx={{ ...SX.ctaGhost, minHeight: 36 }}>Download current TAT</Button>
            <Button variant='outlined' startIcon={<HistoryOutlinedIcon />} onClick={() => setHistoryOpen(true)} sx={{ ...SX.ctaGhost, minHeight: 36 }}>History</Button>
            <Button variant='contained' startIcon={<UploadFileOutlinedIcon />} onClick={() => setUploading(true)} sx={{ ...SX.cta, minHeight: 36 }}>Update custom TAT</Button>
          </Box>
          <CustomLanes grid={grid} baseLabel={basis ? 'Base' : 'Standard'} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1.5 }}>
            <Typography sx={{ ...TYPE.rowHelp, flex: '1 1 320px' }}>
              {grid.latest ? `Last change ${fmtDate(grid.latest.createdAt)}${grid.latest.createdBy ? ` by ${grid.latest.createdBy}` : ''}.` : ''}
              {' '}Changes apply to shipments priced from then on.
            </Typography>
            <Button onClick={() => setResetOpen(true)} sx={{ textTransform: 'none', fontWeight: 600, color: C.danger }}>Remove custom lanes</Button>
          </Box>
        </>
      )}

      <Dialog open={resetOpen} onClose={() => !busy && setResetOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ ...TYPE.sectionTitle, fontSize: 16 }}>Remove the custom lanes of {merchant.name}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...TYPE.rowHelp, fontSize: 13.5, color: C.inkBody }}>
            Its {num(lanes.length)} custom lane{lanes.length === 1 ? '' : 's'} switch off and those lanes go back to {tatBaseName(basis)} for new shipments.
            This cannot be rolled back: to bring the custom lanes back, upload them again. The change is kept in the History.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResetOpen(false)} disabled={busy} sx={{ textTransform: 'none', color: C.inkMuted }}>Cancel</Button>
          <LoadingButton variant='contained' color='error' loading={busy} onClick={reset} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>Remove custom lanes</LoadingButton>
        </DialogActions>
      </Dialog>
      {PolicyDialog}
      <HistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} kind={KIND.TAT} merchantId={merchant.id}
        targetLabel={`${merchant.name} TAT`} />
    </Box>
  )
}
